import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft, Loader2 } from "lucide-react";
import { api, getToken, clearToken } from "@/lib/auth";
import { BrandLogo } from "@/components/BrandLogo";

const B = "/brand";

const GROUPS = [
  {
    title: "Primary Logo — Full Color",
    note: "The master mark. Gold on transparent, plus a framed on-black version.",
    items: [
      { label: "Full color (transparent PNG)", file: "jc-logo-fullcolor.png", bg: "checker" },
      { label: "On black (PNG)", file: "jc-logo-on-black.png", bg: "dark" },
    ],
  },
  {
    title: "Horizontal Header Lockup",
    note: "The master horizontal mark: crest + wordmark with the gold rule set cleanly beneath the name. Used across the site header, footer, letterheads and email signatures.",
    items: [
      { label: "Horizontal master (PNG)", file: "jc-logo-horizontal.png", bg: "dark", wide: true, live: true },
    ],
  },
  {
    title: "Shield-Only Icon",
    note: "The crest without the laurel wreath — for tight spaces and stamps.",
    items: [
      { label: "Shield (transparent PNG)", file: "jc-shield-icon.png", bg: "checker" },
      { label: "Shield · black", file: "jc-shield-black.png", bg: "light" },
      { label: "Shield · white", file: "jc-shield-white.png", bg: "dark" },
    ],
  },
  {
    title: "Favicon & App Icon",
    note: "Browser tab icon and mobile home-screen / social profile tile.",
    items: [
      { label: "App icon 512px (PNG)", file: "jc-app-icon.png", bg: "plain" },
      { label: "favicon.ico", file: "jc-favicon.ico", bg: "dark", download: true, noimg: true },
    ],
  },
  {
    title: "Google Business & Social Cover",
    note: "1920×1080 cover optimized for Google Business Profile — logo centered with generous safe margins so auto-cropping never clips the mark.",
    items: [
      { label: "Google Business cover 1920×1080 (PNG)", file: "jc-google-business-cover.png", bg: "dark", wide: true },
    ],
  },
  {
    title: "Product Photography",
    note: "Full-resolution source photography for each product — clean images with no text, badges or UI overlays. Ideal for Google Business Products, social posts, email and print.",
    items: [
      { label: "Mortgage Protection Insurance", file: "assets/product-mortgage-protection.jpg", bg: "plain" },
      { label: "Term Life", file: "assets/product-term-life.jpg", bg: "plain" },
      { label: "Whole Life", file: "assets/product-whole-life.jpg", bg: "plain" },
      { label: "Universal Life", file: "assets/product-universal-life.jpg", bg: "plain" },
      { label: "Indexed Universal Life", file: "assets/product-indexed-universal-life.jpg", bg: "plain" },
      { label: "Final Expense Insurance", file: "assets/product-final-expense.jpg", bg: "plain" },
      { label: "Fixed Rate Annuities", file: "assets/product-fixed-rate-annuities.jpg", bg: "plain" },
      { label: "Retirement Solutions", file: "assets/product-retirement-solutions.jpg", bg: "plain" },
      { label: "Debt Free 4-Life", file: "assets/product-debt-free-4-life.jpg", bg: "plain" },
    ],
  },
  {
    title: "Photography & Portraits",
    note: "Founder headshot and lifestyle/marketing imagery used across the site — original source files.",
    items: [
      { label: "John Campbell — headshot", file: "assets/headshot-john-campbell.png", bg: "plain" },
      { label: "Family / lifestyle portrait", file: "assets/marketing-family-portrait.jpg", bg: "plain" },
    ],
  },
  {
    title: "One-Color Versions",
    note: "Solid silhouettes for single-color printing, embossing or watermarks.",
    items: [
      { label: "Black (transparent PNG)", file: "jc-logo-black.png", bg: "light" },
      { label: "White (transparent PNG)", file: "jc-logo-white.png", bg: "dark" },
    ],
  },
  {
    title: "Vector — SVG",
    note: "Infinitely scalable single-color vector. Opens & edits in Illustrator (save as .ai/.eps/.pdf).",
    items: [
      { label: "Crest (SVG vector)", file: "jc-crest.svg", bg: "light" },
    ],
  },
];

const bgClass = {
  dark: "bg-[#030303]",
  light: "bg-[#f4f0ea]",
  plain: "bg-[#0a0a0a]",
  checker:
    "bg-[#0a0a0a] [background-image:linear-gradient(45deg,#141414_25%,transparent_25%),linear-gradient(-45deg,#141414_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#141414_75%),linear-gradient(-45deg,transparent_75%,#141414_75%)] [background-size:22px_22px] [background-position:0_0,0_11px,11px_-11px,-11px_0]",
};

export default function BrandKit() {
  const nav = useNavigate();
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login?next=/brand"); return; }
    api.get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => { clearToken(); nav("/admin/login?next=/brand"); });
  }, [nav]);

  if (!authed) {
    return (
      <div className="min-h-screen grain bg-[#030303] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#d4af37]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-[#030303] text-[#f4f0ea]">
      <header className="sticky top-0 z-40 bg-[#030303]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="/" data-testid="brand-back" className="flex items-center gap-3 group">
            <BrandLogo size="sm" />
            <span className="hidden sm:inline-block h-6 w-px bg-white/10" />
            <span className="hidden sm:inline mono text-[9px] uppercase tracking-[0.3em] text-[#d4af37]">Brand Kit</span>
          </a>
          <a href="/" className="mono text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#f4f0ea] flex items-center gap-2">
            <ArrowLeft size={14} /> Back to site
          </a>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Brand Assets</span>
          <h1 className="serif font-light text-5xl lg:text-7xl mt-4 mb-6">The complete <span className="italic text-[#d4af37]">brand library</span>.</h1>
          <p className="text-[#a3a3a3] max-w-2xl leading-relaxed">
            One central home for the John Campbell Insurance identity — logos, colors, fonts, product photography,
            portraits and marketing assets. Download any file individually, or grab the full package. Use these
            originals across your website, Google Business Profile, social media, email and print to keep the
            brand consistent everywhere.
          </p>
          <div className="mt-8 inline-flex items-center gap-4 bg-[#0a0a0a] border border-white/10 rounded-sm px-5 py-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">Palette</div>
              <div className="flex items-center gap-3 mt-2">
                {[["#030303", "Obsidian"], ["#C9A227", "Gold"], ["#F4F0EA", "Ivory"]].map(([hex, name]) => (
                  <div key={hex} className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full border border-white/20" style={{ background: hex }} />
                    <span className="text-xs text-[#a3a3a3]">{name} <span className="text-[#737373]">{hex}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <a
            href={`${B}/john-campbell-brand-kit.zip`}
            download
            data-testid="download-all-zip"
            className="group mt-8 inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
          >
            <Download size={15} /> Download all assets (.zip)
            <span className="transition-transform duration-400 group-hover:translate-x-1">→</span>
          </a>
        </motion.div>

        <div className="mt-16 space-y-16">
          {GROUPS.map((group) => (
            <section key={group.title} data-testid={`brand-group-${group.title.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-baseline gap-4 mb-2">
                <h2 className="serif text-3xl text-[#f4f0ea]">{group.title}</h2>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <p className="text-[#737373] text-sm mb-8 max-w-xl">{group.note}</p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.items.map((it) => {
                  let preview;
                  if (it.noimg) {
                    preview = <div className="mono text-xs uppercase tracking-[0.2em] text-[#737373]">.ICO — multi-size</div>;
                  } else if (it.live) {
                    preview = <BrandLogo size="lg" />;
                  } else {
                    preview = <img src={`${B}/${it.file}`} alt={it.label} className="max-h-full max-w-full object-contain" />;
                  }
                  return (
                  <div key={it.file} className={`${it.wide ? "sm:col-span-2 lg:col-span-3" : ""} bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden group`}>
                    <div className={`relative flex items-center justify-center h-56 p-8 ${bgClass[it.bg]}`}>
                      {it.bg === "checker" && (
                        <span className="absolute top-2 left-3 mono text-[8px] uppercase tracking-[0.2em] text-[#737373]">Transparency preview</span>
                      )}
                      {preview}
                    </div>
                    <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
                      <span className="text-sm text-[#f4f0ea]/85">{it.label}</span>
                      <a
                        href={`${B}/${it.file}`}
                        download
                        data-testid={`download-${it.file}`}
                        className="mono text-[10px] uppercase tracking-[0.2em] text-[#d4af37] hover:text-[#f0d57d] flex items-center gap-2"
                      >
                        <Download size={13} /> Download
                      </a>
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-20 border-t border-white/10 pt-10 text-[#737373] text-sm leading-relaxed max-w-2xl">
          <p><span className="text-[#f4f0ea]">Fonts:</span> Cormorant Garamond (wordmark) · JetBrains Mono (labels) · Manrope (body).</p>
          <p className="mt-2"><span className="text-[#f4f0ea]">Note:</span> the gold master is a high-resolution raster (PNG). The SVG is a clean single-color vector; open it in Adobe Illustrator to export .ai, .eps or .pdf.</p>
        </div>
      </main>
    </div>
  );
}
